<template>
  <div class="container mt-5 px-4">
    <!-- Header Card -->
    <div class="header-card mb-4">
      <div class="d-flex align-items-center">
        <div class="icon-wrapper">
          <i class="bi bi-file-earmark-plus-fill"></i>
        </div>
        <div class="ms-3">
          <h3 class="mb-1 fw-bold">Create Data Template (pre -Modal)</h3>
          <p class="text-muted mb-0">Define a new template for your data structure</p>
        </div>
      </div>
    </div>
    <!-- Main Form Card -->
    <div class="form-card">
      <div class="row g-4">
        <!-- Left Section -->
        <div class="col-lg-6">
          <div class="form-section">
            <h5 class="section-title mb-4">
              <i class="bi bi-gear me-2"></i>Template Configuration
            </h5>

            <div class="mb-4">
              <label class="custom-label">
                <i class="bi bi-tag me-2"></i>Template Name
              </label>
              <input 
                type="text" 
                class="custom-input" 
                placeholder="Enter template name" 
                v-model="template.name"
              >
            </div>

            <!-- Add Item Section -->
            <div class="add-item-section">
              <div class="d-flex align-items-center mb-3">
                <i class="bi bi-plus-circle-fill text-primary me-2" style="font-size: 1.2rem;"></i>
                <h6 class="mb-0 fw-semibold">Add New Item</h6>
              </div>

              <div class="item-form">
                <div class="mb-3">
                  <label class="form-label-small">Label</label>
                  <input 
                    type="text" 
                    class="custom-input-small" 
                    placeholder="e.g. Voltage 1" 
                    v-model="template.label"
                  >
                </div>

                <div class="mb-3">
                  <label class="form-label-small">Key</label>
                  <input 
                    type="text" 
                    class="custom-input-small" 
                    placeholder="e.g. v1" 
                    v-model="template.key"
                  >
                </div>

                <div class="mb-3">
                  <label class="form-label-small">Value Type</label>
                  <select v-model="template.value_type" class="custom-input-small">
                    <option disabled value="">Select type</option>
                    <option>Float</option>
                    <option>Int</option>
                    <option>bool</option>
                  </select>
                </div>

                <div class="mb-3">
                  <label class="form-label-small">Unit</label>
                  <input 
                    type="text" 
                    class="custom-input-small" 
                    placeholder="e.g. Volt (V)" 
                    v-model="template.unit"
                  >
                </div>

                <div class="mb-3">
                  <label class="form-label-small">Widget Type</label>
                  <select v-model="template.widget_type" class="custom-input-small">
                    <option disabled value="">Select widget type</option>
                    <option>realtime</option>
                    <option>gauge</option>
                    <option>chart</option>
                    <option>notification</option>
                  </select>
                </div>

                <div class="text-end">
                  <button 
                    type="button" 
                    class="btn-add-item" 
                    @click="addItemToTemplate()"
                  >
                    <i class="bi bi-plus-lg me-2"></i>Add Item
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Right Section -->
        <div class="col-lg-6">
          <div class="form-section">
            <h5 class="section-title mb-4">
              <i class="bi bi-list-ul me-2"></i>Items List
            </h5>

            <div class="items-container">
              <div v-if="items.length === 0" class="empty-state">
                <i class="bi bi-inbox"></i>
                <p>No items added yet</p>
                <small>Add items using the form on the left</small>
              </div>

              <div v-else class="items-grid">
                <div 
                  class="item-card" 
                  v-for="(item, index) in items" 
                  :key="index"
                >
                  <div class="item-content">
                    <div class="item-label">
                      <i class="bi bi-tag-fill me-2"></i>
                      {{ item.label }}
                    </div>
                    <div class="item-details">
                      <span class="badge-custom">{{ item.value_type }}</span>
                      <span class="badge-custom">{{ item.widget_type }}</span>
                    </div>
                  </div>
                  <button 
                    class="btn-remove" 
                    @click="items.splice(index, 1)"
                  >
                    <i class="bi bi-x-lg"></i>
                  </button>
                </div>
              </div>
            </div>

            <div class="mb-4 mt-4">
              <label class="custom-label">
                <i class="bi bi-chat-left-text me-2"></i>Description
              </label>
              <textarea 
                class="custom-input" 
                rows="5" 
                placeholder="Add template description..." 
                v-model="template.description"
              ></textarea>
            </div>
          </div>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="button-row mt-5">
        <button @click="clearForm()" class="btn-clear">
          <i class="bi bi-arrow-counterclockwise me-2"></i>Clear Form
        </button>
        <button 
          @click="createTemplate()" 
          class="btn-create"
          :disabled="!template.name || items.length === 0"
        >
          <i class="bi bi-check-circle me-2"></i>Create Template
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import CryptoJS from "crypto-js";

export default {
  data() {
    return {
      template: {
        label: "",
        name: "",
        key: "",
        type: "Data",
        value_type: "",
        unit: "",
        widget_type: "",
        description: ""
      },
      items: [],
      device: {
        note: "",
        mode: "api",
        model: "",
      },
      api: {
        mode: "basic",
        in: "",
        key: "",
        value: "",
        username: "",
        password: ""
      },
      mqtt: {
        id: "cxaksjyteqhw",
        topic: "",
        username: "",
        password: ""
      }
    }
  },
  methods: {
    
    addItemToTemplate() {
      if (!this.template.label || !this.template.key || !this.template.value_type || !this.template.widget_type) {
        return;
      }
      
      this.items.push({
        label: this.template.label,
        key: this.template.key,
        value_type: this.template.value_type,
        unit: this.template.unit,
        type: "Data",
        widget_type: this.template.widget_type
      });
      
      this.template.label = "";
      this.template.key = "";
      this.template.value_type = "";
      this.template.unit = "";
      this.template.widget_type = "";
    },

    clearForm() {
      this.template.name = "";
      this.template.description = "";
      this.template.label = "";
      this.template.key = "";
      this.template.value_type = "";
      this.template.unit = "";
      this.template.widget_type = "";
      this.items = [];
    },

    generateKey_MQTT() {
      this.mqtt.id = CryptoJS.lib.WordArray.random(8).toString(CryptoJS.enc.Base64);
      this.mqtt.username = CryptoJS.lib.WordArray.random(12).toString(CryptoJS.enc.Base64);
      this.mqtt.password = CryptoJS.lib.WordArray.random(12).toString(CryptoJS.enc.Base64);
    },

    copyTextFallback(text) {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
    },

    async createTemplate() {
      try {
        var accessToken = localStorage.getItem("accessToken");
        const response = await fetch(`${process.env.VUE_APP_API_URL}/api/devices/template`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`
          },
          body: JSON.stringify({
            "name": this.template.name,
            "description": this.template.description,
            "items": this.items
          })
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        this.$router.push('/settings/device/listtemplate');
        console.log('Dashboard page created:', result);
      } catch (error) {
        console.error('Error creating dashboard page:', error);
      }
    },
  },
  created() {
  }
}
</script>

<style scoped>

/* Header Card */
.header-card {
  background: linear-gradient(135deg, #66b3ea 0%, #4b68a2 100%);
  padding: 2rem;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(102, 126, 234, 0.15);
  color: white;
}

.icon-wrapper {
  width: 60px;
  height: 60px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.8rem;
  backdrop-filter: blur(10px);
}

.header-card h3 {
  color: white;
  margin: 0;
}

.header-card p {
  color: rgba(255, 255, 255, 0.9);
}

/* Form Card */
.form-card {
  background: white;
  padding: 2.5rem;
  border-radius: 16px;
  box-shadow: 0 2px 20px rgba(0, 0, 0, 0.06);
}

.form-section {
  height: 100%;
}

.section-title {
  color: #2d3748;
  font-weight: 600;
  font-size: 1.1rem;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid #e2e8f0;
}

/* Custom Input Styles */
.custom-label {
  display: block;
  font-weight: 500;
  color: #4a5568;
  margin-bottom: 0.5rem;
  font-size: 0.95rem;
}

.custom-input {
  width: 100%;
  padding: 0.85rem 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  font-size: 0.95rem;
  transition: all 0.3s ease;
  background: #fafafa;
}

.custom-input:focus {
  outline: none;
  border-color: #667eea;
  background: white;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.custom-input::placeholder {
  color: #a0aec0;
}

textarea.custom-input {
  resize: vertical;
}

/* Add Item Section */
.add-item-section {
  background: #f7fafc;
  padding: 1.5rem;
  border-radius: 12px;
  border: 2px solid #e2e8f0;
  margin-top: 1.5rem;
}

.item-form {
  margin-top: 1rem;
}

.form-label-small {
  display: block;
  font-weight: 500;
  color: #4a5568;
  margin-bottom: 0.4rem;
  font-size: 0.85rem;
}

.custom-input-small {
  width: 100%;
  padding: 0.65rem 0.85rem;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  background: white;
}

.custom-input-small:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

select.custom-input-small {
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%234a5568' d='M6 9L1 4h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.85rem center;
  padding-right: 2.5rem;
}

.btn-add-item {
  padding: 0.65rem 1.5rem;
  border: none;
  background: linear-gradient(135deg, #66b3ea 0%, #4b68a2 100%);
  color: white;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  font-size: 0.9rem;
}

.btn-add-item:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

/* Items Container */
.items-container {
  background: #f7fafc;
  border-radius: 12px;
  padding: 1.5rem;
  min-height: 300px;
  max-height: 300px;
  overflow-y: auto;
  border: 2px solid #e2e8f0;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #a0aec0;
  text-align: center;
}

.empty-state i {
  font-size: 3rem;
  margin-bottom: 1rem;
  opacity: 0.5;
}

.empty-state p {
  margin: 0;
  font-weight: 500;
  font-size: 1rem;
}

.empty-state small {
  font-size: 0.85rem;
}

.items-grid {
  display: grid;
  gap: 0.75rem;
}

.item-card {
  background: white;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  padding: 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.3s ease;
}

.item-card:hover {
  border-color: #cbd5e0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.item-content {
  flex: 1;
}

.item-label {
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
}

.item-label i {
  color: #667eea;
  font-size: 0.9rem;
}

.item-details {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.badge-custom {
  background: linear-gradient(135deg, #66b3ea 0%, #4b68a2 100%);
  color: white;
  padding: 0.25rem 0.65rem;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 500;
}

.btn-remove {
  background: #fed7d7;
  border: none;
  color: #c53030;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.btn-remove:hover {
  background: #fc8181;
  color: white;
  transform: scale(1.1);
}

/* Scrollbar Styling */
.items-container::-webkit-scrollbar {
  width: 8px;
}

.items-container::-webkit-scrollbar-track {
  background: #e2e8f0;
  border-radius: 10px;
}

.items-container::-webkit-scrollbar-thumb {
  background: #cbd5e0;
  border-radius: 10px;
}

.items-container::-webkit-scrollbar-thumb:hover {
  background: #a0aec0;
}

/* Buttons */
.button-row {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  padding-top: 2rem;
  border-top: 2px solid #e2e8f0;
}

.btn-clear {
  padding: 0.85rem 2rem;
  border: 2px solid #e2e8f0;
  background: white;
  color: #718096;
  border-radius: 10px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
}

.btn-clear:hover {
  background: #f7fafc;
  border-color: #cbd5e0;
  color: #2d3748;
  transform: translateY(-1px);
}

.btn-create {
  padding: 0.85rem 2.5rem;
  border: none;
  background: linear-gradient(135deg, #66b3ea 0%, #4b68a2 100%);
  color: white;
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
}

.btn-create:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
}

.btn-create:disabled {
  background: #cbd5e0;
  cursor: not-allowed;
  box-shadow: none;
}

/* Responsive */
@media (max-width: 991px) {
  .form-card {
    padding: 1.5rem;
  }
  
  .header-card {
    padding: 1.5rem;
  }
  
  .button-row {
    flex-direction: column-reverse;
  }
  
  .btn-clear,
  .btn-create {
    width: 100%;
    justify-content: center;
  }
}
</style>